import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useAuth } from "@/context/AuthContext";
import DashboardLayout from "@/components/dashboard/DashboardLayout";
import axios from "axios";
import { 
  FileText, 
  MessageSquare, 
  CreditCard, 
  TrendingUp,
  Clock,
  CheckCircle2,
  AlertCircle,
  ArrowRight,
  PiggyBank,
  Calendar
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STATUS_LABELS = {
  submitted: { label: "Application Submitted", color: "bg-blue-500" },
  documents_required: { label: "Documents Required", color: "bg-amber-500" },
  under_review: { label: "Under Review", color: "bg-purple-500" },
  proposal_drafting: { label: "Proposal Being Drafted", color: "bg-indigo-500" },
  creditor_voting: { label: "Creditor Voting", color: "bg-cyan-500" },
  active: { label: "IVA Active", color: "bg-[#10B981]" },
  completed: { label: "Completed", color: "bg-green-600" }
};

const DashboardOverview = () => {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [caseData, setCaseData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, [token]);

  const fetchData = async () => {
    try {
      const [statsRes, caseRes] = await Promise.all([
        axios.get(`${API}/dashboard/stats`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/cases/my-case`, {
          headers: { Authorization: `Bearer ${token}` }
        }).catch(() => null)
      ]);
      
      setStats(statsRes.data);
      if (caseRes) setCaseData(caseRes.data);
    } catch (error) {
      console.error("Dashboard error:", error);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value || 0);
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="w-8 h-8 border-4 border-[#10B981] border-t-transparent rounded-full animate-spin"></div>
        </div>
      </DashboardLayout>
    );
  }

  // No case yet - show start case CTA
  if (!stats?.has_case) {
    return (
      <DashboardLayout>
        <div className="max-w-2xl mx-auto text-center py-12" data-testid="no-case-view">
          <div className="w-20 h-20 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <FileText className="w-10 h-10 text-[#10B981]" />
          </div>
          <h2 className="text-2xl font-bold text-[#0F172A] mb-4">Start Your IVA Application</h2>
          <p className="text-slate-600 mb-8">
            You haven't started your IVA application yet. Begin now to get your personalised debt solution.
          </p>
          <Button
            onClick={() => navigate("/dashboard/start-case")}
            className="bg-[#10B981] hover:bg-[#059669] text-white px-8 py-3"
            data-testid="start-case-btn"
          >
            Start Application
            <ArrowRight className="w-5 h-5 ml-2" />
          </Button>
        </div>
      </DashboardLayout>
    );
  }

  const statusInfo = STATUS_LABELS[stats.case_status] || STATUS_LABELS.submitted;

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="dashboard-overview">
        {/* Status Banner */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-2xl p-6 border border-slate-200"
        >
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className={`w-12 h-12 ${statusInfo.color} rounded-xl flex items-center justify-center`}>
                {stats.case_status === "active" ? (
                  <CheckCircle2 className="w-6 h-6 text-white" />
                ) : stats.case_status === "documents_required" ? (
                  <AlertCircle className="w-6 h-6 text-white" />
                ) : (
                  <Clock className="w-6 h-6 text-white" />
                )}
              </div>
              <div>
                <p className="text-sm text-slate-500">Case Status</p>
                <p className="text-xl font-bold text-[#0F172A]">{statusInfo.label}</p>
              </div>
            </div>
            <div className="flex-1 max-w-md">
              <div className="flex justify-between text-sm mb-2">
                <span className="text-slate-500">Progress</span>
                <span className="font-semibold text-[#0F172A]">{stats.progress_percentage}%</span>
              </div>
              <Progress value={stats.progress_percentage} className="h-3" />
            </div>
          </div>
          
          {stats.case_status === "documents_required" && stats.documents_required > 0 && (
            <div className="mt-4 p-4 bg-amber-50 rounded-xl border border-amber-200">
              <div className="flex items-center gap-3">
                <AlertCircle className="w-5 h-5 text-amber-600" />
                <p className="text-amber-800">
                  <span className="font-semibold">{stats.documents_required} document(s)</span> still required to proceed with your application.
                </p>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => navigate("/dashboard/documents")}
                  className="ml-auto border-amber-300 text-amber-700 hover:bg-amber-100"
                >
                  Upload Now
                </Button>
              </div>
            </div>
          )}
        </motion.div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-white rounded-2xl p-6 border border-slate-200"
          >
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-slate-100 rounded-lg flex items-center justify-center">
                <CreditCard className="w-5 h-5 text-slate-600" />
              </div>
              <span className="text-sm text-slate-500">Total Debt</span>
            </div>
            <p className="font-numbers text-3xl font-bold text-[#0F172A]" data-testid="total-debt">
              {formatCurrency(stats.debt_amount)}
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-white rounded-2xl p-6 border border-slate-200"
          >
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center">
                <PiggyBank className="w-5 h-5 text-[#10B981]" />
              </div>
              <span className="text-sm text-slate-500">Est. Write-Off</span>
            </div>
            <p className="font-numbers text-3xl font-bold text-[#10B981]" data-testid="write-off">
              {formatCurrency(stats.estimated_write_off)}
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="bg-white rounded-2xl p-6 border border-slate-200"
          >
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                <Calendar className="w-5 h-5 text-blue-600" />
              </div>
              <span className="text-sm text-slate-500">Monthly Payment</span>
            </div>
            <p className="font-numbers text-3xl font-bold text-[#0F172A]" data-testid="monthly-payment">
              {formatCurrency(stats.estimated_monthly_payment)}
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="bg-white rounded-2xl p-6 border border-slate-200"
          >
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                <TrendingUp className="w-5 h-5 text-purple-600" />
              </div>
              <span className="text-sm text-slate-500">Payments Made</span>
            </div>
            <p className="font-numbers text-3xl font-bold text-[#0F172A]">
              {stats.payments_made} <span className="text-lg text-slate-400">/ {stats.iva_term_months}</span>
            </p>
          </motion.div>
        </div>

        {/* Quick Actions */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <motion.button
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            onClick={() => navigate("/dashboard/documents")}
            className="bg-white rounded-2xl p-6 border border-slate-200 text-left hover:border-[#10B981] hover:shadow-lg transition-all group"
            data-testid="quick-action-documents"
          >
            <div className="flex items-center justify-between mb-4">
              <div className="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center">
                <FileText className="w-6 h-6 text-amber-600" />
              </div>
              <ArrowRight className="w-5 h-5 text-slate-300 group-hover:text-[#10B981] transition-colors" />
            </div>
            <h3 className="font-semibold text-[#0F172A] mb-1">Documents</h3>
            <p className="text-sm text-slate-500">
              {stats.documents_required > 0 
                ? `${stats.documents_required} required` 
                : `${stats.total_documents} uploaded`}
            </p>
          </motion.button>

          <motion.button
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6 }}
            onClick={() => navigate("/dashboard/messages")}
            className="bg-white rounded-2xl p-6 border border-slate-200 text-left hover:border-[#10B981] hover:shadow-lg transition-all group"
            data-testid="quick-action-messages"
          >
            <div className="flex items-center justify-between mb-4">
              <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                <MessageSquare className="w-6 h-6 text-blue-600" />
              </div>
              <ArrowRight className="w-5 h-5 text-slate-300 group-hover:text-[#10B981] transition-colors" />
            </div>
            <h3 className="font-semibold text-[#0F172A] mb-1">Messages</h3>
            <p className="text-sm text-slate-500">
              {stats.unread_messages > 0 
                ? `${stats.unread_messages} unread` 
                : "Chat with your advisor"}
            </p>
          </motion.button>

          <motion.button
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.7 }}
            onClick={() => navigate("/dashboard/debts")}
            className="bg-white rounded-2xl p-6 border border-slate-200 text-left hover:border-[#10B981] hover:shadow-lg transition-all group"
            data-testid="quick-action-debts"
          >
            <div className="flex items-center justify-between mb-4">
              <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center">
                <CreditCard className="w-6 h-6 text-purple-600" />
              </div>
              <ArrowRight className="w-5 h-5 text-slate-300 group-hover:text-[#10B981] transition-colors" />
            </div>
            <h3 className="font-semibold text-[#0F172A] mb-1">My Debts</h3>
            <p className="text-sm text-slate-500">View and manage creditors</p>
          </motion.button>
        </div>

        {/* Advisor Card */}
        {caseData?.advisor_name && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.8 }}
            className="bg-gradient-to-r from-[#0F172A] to-slate-800 rounded-2xl p-6 text-white"
          >
            <div className="flex flex-col md:flex-row md:items-center gap-6">
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 bg-[#10B981] rounded-full flex items-center justify-center text-2xl font-bold">
                  {caseData.advisor_name.charAt(0)}
                </div>
                <div>
                  <p className="text-slate-400 text-sm">Your Dedicated Advisor</p>
                  <p className="text-xl font-semibold">{caseData.advisor_name}</p>
                  <p className="text-slate-400 text-sm">{caseData.advisor_email}</p>
                </div>
              </div>
              <div className="md:ml-auto">
                <Button
                  onClick={() => navigate("/dashboard/messages")}
                  className="bg-[#10B981] hover:bg-[#059669] text-white"
                >
                  <MessageSquare className="w-4 h-4 mr-2" />
                  Send Message
                </Button>
              </div>
            </div>
          </motion.div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default DashboardOverview;
